import React, { useEffect } from 'react'

import PageWrapper from 'enrollment-platform/PageWrapper'
import { accessControlApi } from 'enrollment-platform/pages/AccessControl/api/access-control.client'
import { queryClient } from 'lib/queryClient'

import { usePermission } from './usePermission'
import { MY_ABILITY_QUERY_KEY } from './useMyAbilityQuery'
import { NoAdminAccess } from './NoAdminAccess'
import { RbacActions, RbacSubjects } from './constants'

interface Props {
  children: React.ReactNode
}

/**
 * Route-level entry gate for enrollment-platform admin surfaces. Used as a
 * route `component` in place of PageWrapper (which it renders internally),
 * mirroring the existing CustomerGate / PlanManagementV2Gate convention so it
 * mounts exactly once per route — no nesting.
 *
 * Admits a user only if they can `Access` the `AdminPortal`. RBAC is purely
 * DB-driven — every role, including PlatformHixmeAdmin, needs an explicit
 * AdminPortal/Access grant (no role-based wildcard). UX only — the EPS
 * AbilityGuard is the real
 * boundary. Eagerly warms the ability cache on mount so the gate and downstream
 * permission-gated UI don't flash.
 */
export default function AdminPortalGate({ children }: Props) {
  useEffect(() => {
    // Warm the ability cache on mount so the gate + downstream permission-gated
    // UI don't flash. RBAC is always enforced on the client (mirrors the server).
    queryClient.prefetchQuery({
      queryKey: MY_ABILITY_QUERY_KEY,
      queryFn: () => accessControlApi.getMyAbility(),
    })
  }, [])
  const { allowed, isLoading, isError } = usePermission(
    RbacActions.Access,
    RbacSubjects.AdminPortal
  )
  if (isLoading) {
    return <PageWrapper>{null}</PageWrapper>
  }
  if (isError || !allowed) {
    return (
      <PageWrapper>
        <NoAdminAccess />
      </PageWrapper>
    )
  }
  return <PageWrapper>{children}</PageWrapper>
}
